import type { Ref } from 'vue'

import { useStorage } from '@vueuse/core'
import { computed, reactive, ref } from 'vue'

import type { AppState, Color, LayoutState, MenuRouteContext } from '@/types/layout'

import { setLocale } from '@/i18n'

const appState: Ref<AppState> = useStorage('theme-settings', {
  primary: 'kit',
  darkMode: false,
  locale: 'de',
})

const layoutState: LayoutState = reactive({
  staticMenuDesktopInactive: false,
  overlayMenuActive: false,
  profileSidebarVisible: false,
  configSidebarVisible: false,
  staticMenuMobileActive: false,
  menuHoverActive: false,
  activeMenuItem: null as string | null,
})

// Override for the path the menu matches its items against, see MenuRouteContext
const menuRouteContext: Ref<MenuRouteContext | null> = ref(null)

const primaryColors: Ref<Color[]> = ref([
  {
    name: 'noir',
    palette: {
      50: '#fafafa',
      100: '#f4f4f5',
      200: '#e4e4e7',
      300: '#d4d4d8',
      400: '#a1a1aa',
      500: '#71717a',
      600: '#52525b',
      700: '#3f3f46',
      800: '#27272a',
      900: '#18181b',
      950: '#09090b',
    },
    hueRotate: 0,
  },
  {
    name: 'kit',
    palette: {
      50: '#c7fff2',
      100: '#90ffe4',
      200: '#51f7d6',
      300: '#1de4c2',
      400: '#04c8a9',
      500: '#009682',
      600: '#058071',
      700: '#0a655b',
      800: '#0d544c',
      900: '#0d544c',
      950: '#003330',
    },
    hueRotate: 0,
  },
  {
    name: 'green',
    palette: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16',
    },
  },
  {
    name: 'lime',
    palette: {
      50: '#f7fee7',
      100: '#ecfccb',
      200: '#d9f99d',
      300: '#bef264',
      400: '#a3e635',
      500: '#84cc16',
      600: '#65a30d',
      700: '#4d7c0f',
      800: '#3f6212',
      900: '#365314',
      950: '#1a2e05',
    },
  },
  {
    name: 'orange',
    palette: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407',
    },
  },
  {
    name: 'amber',
    palette: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03',
    },
  },

  {
    name: 'sky',
    palette: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
      950: '#082f49',
    },
  },
  {
    name: 'blue',
    palette: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
  },
  {
    name: 'indigo',
    palette: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
      950: '#1e1b4b',
    },
  },

  {
    name: 'purple',
    palette: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87',
      950: '#3b0764',
    },
  },
  {
    name: 'fuchsia',
    palette: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
      950: '#4a044e',
    },
  },
  {
    name: 'pink',
    palette: {
      50: '#fdf2f8',
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',
      800: '#9d174d',
      900: '#831843',
      950: '#500724',
    },
  },
])

const paletteSteps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const

function applyPrimaryPalette(palette: Color['palette']) {
  for (const step of paletteSteps) {
    const value = palette[step]
    if (value) {
      document.documentElement.style.setProperty(`--ui-color-primary-${step}`, value)
    }
  }
}

function applyDarkMode(enabled: boolean) {
  document.documentElement.classList.toggle('dark', enabled)
  document.documentElement.style.colorScheme = enabled ? 'dark' : 'light'
}

export function useLayout() {
  const setActiveMenuItem = (item: unknown) => {
    if (typeof item === 'object' && item !== null && 'value' in item) {
      const value = (item as { value?: unknown }).value
      layoutState.activeMenuItem = typeof value === 'string' ? value : null
      return
    }
    layoutState.activeMenuItem = typeof item === 'string' ? item : null
  }
  const setMenuRouteContext = (context: MenuRouteContext | null) => {
    menuRouteContext.value = context
  }
  const toggleMenu = () => {
    if (window.innerWidth > 991) {
      layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive
    } else {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive
    }
  }

  const isSidebarActive = computed(
    () => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
  )

  function setPrimaryColor(colorName: string) {
    appState.value.primary = colorName
    const color = primaryColors.value.find((c: Color) => c.name === colorName)
    if (color) {
      applyPrimaryPalette(color.palette)
    }
  }

  function toggleDarkMode() {
    appState.value.darkMode = !appState.value.darkMode
    applyDarkMode(appState.value.darkMode)
  }
  function toggleLanguage() {
    appState.value.locale = appState.value.locale === 'de' ? 'en' : 'de'
    setLocale(appState.value.locale)
  }

  function setLanguage(value: 'de' | 'en') {
    appState.value.locale = value
    setLocale(appState.value.locale)
  }

  function initTheme() {
    applyDarkMode(appState.value.darkMode)
    // `kit` is already the build-time primary (see the `ui.colors` block in vite.config.ts),
    // as is the `zinc` neutral, so only a deviating primary needs a runtime override.
    if (appState.value.primary && appState.value.primary !== 'kit') {
      setPrimaryColor(appState.value.primary)
    }
    setLocale(appState.value.locale)
  }

  const isDarkMode = computed(() => appState.value.darkMode)
  const primary = computed(() => appState.value.primary)

  return {
    primaryColors,
    isDarkMode,
    primary,
    toggleDarkMode,
    setPrimaryColor,
    initTheme,
    toggleLanguage,
    setLanguage,
    setActiveMenuItem,
    menuRouteContext,
    setMenuRouteContext,
    isSidebarActive,
    layoutState,
    toggleMenu,
  }
}
